import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api/client';
import './DomainInventory.css';

/* ─── Types ──────────────────────────────────────────────── */

type RunStatus = 'queued' | 'running' | 'completed' | 'failed';
type ItemStatus = 'pending' | 'ok' | 'error';

interface InventorySnapshot {
  run_id: string | null;
  panels_total: number;
  domains_total: number;
  primary_total: number;
  panels_errored: number;
  pulled_at: string | null;
  status: RunStatus | null;
}

interface InventoryRun {
  id: string;
  status: RunStatus;
  triggered_by: string | null;
  parent_removal_run_id: string | null;
  panels_total: number;
  panels_done: number;
  panels_errored: number;
  domains_total: number;
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

interface InventoryRunItem {
  run_id: string;
  inventory_panel_id: string;
  panel_email: string;
  panel_reseller_id: string | null;
  status: ItemStatus;
  domains_found: number | null;
  primary_domain: string | null;
  error_code: string | null;
  error_message: string | null;
  polled_at: string | null;
}

interface RunDetail {
  run: InventoryRun;
  items: InventoryRunItem[];
}

interface DomainSearchHit {
  domain_name: string;
  is_primary: boolean;
  panels: { inventory_panel_id: string; email: string; reseller_id: string | null }[];
}

interface Toast {
  type: 'success' | 'error';
  message: string;
}

/* ─── Helpers ────────────────────────────────────────────── */

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return new Date(iso).toLocaleDateString();
}

function durationLabel(start: string | null, end: string | null): string {
  if (!start) return '—';
  const endMs = end ? new Date(end).getTime() : Date.now();
  const ms = endMs - new Date(start).getTime();
  if (ms < 1000) return '<1s';
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const restSec = sec % 60;
  return restSec === 0 ? `${min}m` : `${min}m ${restSec}s`;
}

const PlayIcon = (): JSX.Element => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

const RefreshIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 4v6h-6M1 20v-6h6" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const ChevronIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const INITIAL_VISIBLE = 15;
const PAGE_INCREMENT = 40;

/* ─── Main component ─────────────────────────────────────── */

export function DomainInventory(): JSX.Element {
  const [snapshot, setSnapshot] = useState<InventorySnapshot | null>(null);
  const [runs, setRuns] = useState<InventoryRun[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<RunDetail | null>(null);
  const [starting, setStarting] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchHits, setSearchHits] = useState<DomainSearchHit[]>([]);

  const showToast = useCallback((type: Toast['type'], message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 4500);
  }, []);

  const refreshSnapshot = useCallback(async (): Promise<void> => {
    try {
      const data = await api<InventorySnapshot>('/api/cleanup/inventory/latest');
      setSnapshot(data);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to load snapshot');
    }
  }, [showToast]);

  const refreshList = useCallback(async (): Promise<void> => {
    try {
      const data = await api<InventoryRun[]>('/api/cleanup/inventory/runs');
      setRuns((prev) => {
        if (!prev) return data;
        const headIds = new Set(data.map((r) => r.id));
        const older = prev.filter((r) => !headIds.has(r.id));
        return [...data, ...older];
      });
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to load runs');
    }
  }, [showToast]);

  /* List polling: 3s while inflight, 7s otherwise. Same cadence as License Assignment. */
  useEffect(() => {
    void refreshSnapshot();
    void refreshList();
  }, [refreshSnapshot, refreshList]);

  const listPollIntervalRef = useRef<number | null>(null);
  useEffect(() => {
    if (selectedId !== null) return;
    const hasInflight = runs?.some((r) => r.status === 'queued' || r.status === 'running');
    const intervalMs = hasInflight ? 3000 : 7000;
    if (listPollIntervalRef.current !== null) {
      window.clearInterval(listPollIntervalRef.current);
    }
    listPollIntervalRef.current = window.setInterval(() => {
      void refreshList();
      void refreshSnapshot();
    }, intervalMs);
    return () => {
      if (listPollIntervalRef.current !== null) {
        window.clearInterval(listPollIntervalRef.current);
        listPollIntervalRef.current = null;
      }
    };
  }, [runs, selectedId, refreshList, refreshSnapshot]);

  /* Detail polling: 2.5s until terminal status. */
  const refreshDetail = useCallback(
    async (id: string): Promise<void> => {
      try {
        const data = await api<RunDetail>(`/api/cleanup/inventory/runs/${id}`);
        setDetail(data);
      } catch (err) {
        showToast('error', err instanceof Error ? err.message : 'Failed to load run');
      }
    },
    [showToast],
  );

  const detailPollIntervalRef = useRef<number | null>(null);
  useEffect(() => {
    if (selectedId === null) {
      setDetail(null);
      if (detailPollIntervalRef.current !== null) {
        window.clearInterval(detailPollIntervalRef.current);
        detailPollIntervalRef.current = null;
      }
      return;
    }
    void refreshDetail(selectedId);
    detailPollIntervalRef.current = window.setInterval(() => {
      void refreshDetail(selectedId);
    }, 2500);
    return () => {
      if (detailPollIntervalRef.current !== null) {
        window.clearInterval(detailPollIntervalRef.current);
        detailPollIntervalRef.current = null;
      }
    };
  }, [selectedId, refreshDetail]);

  useEffect(() => {
    if (!detail) return;
    if (detail.run.status === 'completed' || detail.run.status === 'failed') {
      if (detailPollIntervalRef.current !== null) {
        window.clearInterval(detailPollIntervalRef.current);
        detailPollIntervalRef.current = null;
      }
    }
  }, [detail]);

  /* Domain search (debounced). */
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchHits([]);
      return;
    }
    const handle = window.setTimeout(async () => {
      try {
        const data = await api<DomainSearchHit[]>(
          `/api/cleanup/inventory/domains?q=${encodeURIComponent(q)}`,
        );
        setSearchHits(data);
      } catch (err) {
        showToast('error', err instanceof Error ? err.message : 'Search failed');
      }
    }, 250);
    return () => window.clearTimeout(handle);
  }, [searchQuery, showToast]);

  const startRun = async (): Promise<void> => {
    setStarting(true);
    try {
      const result = await api<{ runId: string; panelsQueued: number }>(
        '/api/cleanup/inventory/runs',
        { method: 'POST', body: JSON.stringify({}) },
      );
      showToast('success', `Started - ${result.panelsQueued} panels queued`);
      await refreshList();
      setSelectedId(result.runId);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'RUN_IN_PROGRESS') {
        const existingRunId = (err.data as { existingRunId?: string } | undefined)?.existingRunId;
        if (existingRunId) {
          showToast('success', 'A pull is already in progress - opening it now.');
          await refreshList();
          setSelectedId(existingRunId);
          return;
        }
      }
      const msg =
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Failed to start run';
      showToast('error', msg);
    } finally {
      setStarting(false);
    }
  };

  /* Render: detail or list */
  if (selectedId !== null) {
    return (
      <div className="cln-root">
        <div className="cln-scroll">
          <header className="cln-header">
            <button
              type="button"
              className="cln-back"
              onClick={() => setSelectedId(null)}
            >
              <ChevronIcon />
              <span>Back to runs</span>
            </button>
          </header>
          {detail ? (
            <RunDetailView detail={detail} onRefresh={() => void refreshDetail(detail.run.id)} />
          ) : (
            <div className="cln-loading">Loading run…</div>
          )}
        </div>
        {toast && <div className={`cln-toast cln-toast--${toast.type}`}>{toast.message}</div>}
      </div>
    );
  }

  const totalRuns = runs?.length ?? 0;
  const visibleRuns = runs ? runs.slice(0, visibleCount) : null;
  const inflight = runs?.find((r) => r.status === 'queued' || r.status === 'running') ?? null;

  return (
    <div className="cln-root">
      <div className="cln-scroll">
        <header className="cln-header">
          <div className="cln-header-row">
            <div>
              <h1 className="cln-title">Domain Inventory</h1>
              <p className="cln-subtitle">
                Catalog of which domains live in which inventory panel. Auto-refreshed by every
                Domain Removal run; pull manually anytime.
              </p>
            </div>
            <button
              type="button"
              className="cln-btn cln-btn--primary"
              onClick={startRun}
              disabled={starting || inflight !== null}
              title={inflight ? 'A pull is already in progress' : 'Refresh the panel_domains cache'}
            >
              <PlayIcon />
              {starting ? 'Starting…' : inflight ? 'Pull in progress…' : 'Run Inventory Pull'}
            </button>
          </div>
        </header>

        {/* Snapshot card */}
        {snapshot && (
          <section className="cln-card cln-snapshot-card">
            <div className="cln-snapshot-meta">
              {snapshot.pulled_at
                ? <>Current snapshot - pulled {relativeTime(snapshot.pulled_at)}</>
                : <>No snapshot yet - click <strong>Run Inventory Pull</strong> to fetch one.</>}
            </div>
            <div className="cln-snapshot-grid">
              <SnapshotStat label="Inventory panels" value={snapshot.panels_total} />
              <SnapshotStat label="Domains" value={snapshot.domains_total} variant="ink" />
              <SnapshotStat label="Primary" value={snapshot.primary_total} variant="muted" />
              <SnapshotStat
                label="Errored panels"
                value={snapshot.panels_errored}
                variant={snapshot.panels_errored > 0 ? 'rose' : 'muted'}
              />
            </div>
          </section>
        )}

        {/* Search box */}
        <section className="cln-search-card">
          <input
            type="text"
            className="cln-search-input"
            placeholder="Search domains across all panels…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchHits.length > 0 && (
            <table className="cln-table cln-search-results">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Type</th>
                  <th>Panels</th>
                </tr>
              </thead>
              <tbody>
                {searchHits.map((h) => (
                  <tr key={h.domain_name}>
                    <td className="cln-cell-mono">{h.domain_name}</td>
                    <td>
                      <span className={`cln-badge cln-badge--${h.is_primary ? 'honey' : 'muted'}`}>
                        {h.is_primary ? 'primary' : 'secondary'}
                      </span>
                    </td>
                    <td className="cln-cell-faint">
                      {h.panels.map((p) => `${p.reseller_id ?? '—'} (${p.email})`).join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Runs list */}
        <section className="cln-card">
          <div className="cln-card-header">
            <h2 className="cln-card-title">
              Runs <span className="cln-cell-faint">({Math.min(visibleCount, totalRuns)} of {totalRuns})</span>
            </h2>
            <button
              type="button"
              className="cln-icon-btn"
              onClick={() => void refreshList()}
              title="Refresh list"
            >
              <RefreshIcon />
            </button>
          </div>

          {runs === null && <div className="cln-empty">Loading…</div>}

          {runs !== null && runs.length === 0 && (
            <div className="cln-empty">
              <h3 className="cln-empty-title">No runs yet</h3>
              <p className="cln-empty-text">
                Click <strong>Run Inventory Pull</strong> above to populate the cache.
              </p>
            </div>
          )}

          {visibleRuns !== null && visibleRuns.length > 0 && (
            <table className="cln-table">
              <thead>
                <tr>
                  <th>Run</th>
                  <th>Status</th>
                  <th className="cln-col-num">Panels</th>
                  <th className="cln-col-num">Domains</th>
                  <th>Triggered by</th>
                  <th className="cln-col-date">Started</th>
                  <th className="cln-col-date">Duration</th>
                </tr>
              </thead>
              <tbody>
                {visibleRuns.map((r) => (
                  <tr key={r.id} className="cln-row" onClick={() => setSelectedId(r.id)}>
                    <td className="cln-cell-mono cln-cell-faint">{r.id.slice(0, 8)}</td>
                    <td>
                      <span className={`cln-badge cln-badge--${r.status}`}>{r.status}</span>
                    </td>
                    <td className="cln-col-num">
                      <span className="cln-num">{r.panels_total}</span>
                      {r.status === 'completed' && r.panels_errored > 0 && (
                        <span className="cln-num-detail"> · {r.panels_errored} err</span>
                      )}
                    </td>
                    <td className="cln-col-num cln-num--ink">{r.domains_total}</td>
                    <td className="cln-cell-mono cln-cell-faint">
                      {r.parent_removal_run_id ? `removal-run/${r.parent_removal_run_id.slice(0, 8)}` : (r.triggered_by ?? '—')}
                    </td>
                    <td className="cln-col-date cln-cell-faint">
                      {relativeTime(r.started_at ?? r.created_at)}
                    </td>
                    <td className="cln-col-date cln-cell-faint">
                      {durationLabel(r.started_at, r.finished_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {visibleRuns !== null && totalRuns > visibleCount && (
            <div className="cln-loadmore-row">
              <button
                type="button"
                className="cln-btn cln-btn--ghost"
                onClick={() => setVisibleCount((c) => Math.min(c + PAGE_INCREMENT, totalRuns))}
              >
                Load more
              </button>
            </div>
          )}
        </section>
      </div>
      {toast && <div className={`cln-toast cln-toast--${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

function SnapshotStat({
  label,
  value,
  variant = 'default',
}: {
  label: string;
  value: number;
  variant?: 'default' | 'ink' | 'rose' | 'muted';
}): JSX.Element {
  return (
    <div className="cln-snapshot-stat">
      <span className="cln-snapshot-stat-label">{label}</span>
      <span className={`cln-snapshot-stat-value cln-snapshot-stat-value--${variant}`}>{value}</span>
    </div>
  );
}

function RunDetailView({
  detail,
  onRefresh,
}: {
  detail: RunDetail;
  onRefresh: () => void;
}): JSX.Element {
  const { run, items } = detail;
  const sortRank = (s: ItemStatus): number => (s === 'error' ? 0 : s === 'pending' ? 1 : 2);
  const sortedItems = [...items].sort((a, b) => sortRank(a.status) - sortRank(b.status));

  return (
    <>
      <section className="cln-summary-card">
        <div className="cln-summary-top">
          <div>
            <h2 className="cln-summary-title">
              Run <span className="cln-cell-mono cln-cell-faint">{run.id.slice(0, 8)}</span>
            </h2>
            <div className="cln-summary-meta">
              {run.parent_removal_run_id ? <>Triggered by <strong>removal-run/{run.parent_removal_run_id.slice(0, 8)}</strong></> :
                run.triggered_by ? <>Triggered by <strong>{run.triggered_by}</strong></> : null}
              {' '}· {relativeTime(run.created_at)}
              {run.started_at && <> · ran {durationLabel(run.started_at, run.finished_at)}</>}
            </div>
          </div>
          <div className="cln-summary-actions">
            <span className={`cln-badge cln-badge--${run.status}`}>{run.status}</span>
            <button type="button" className="cln-icon-btn" onClick={onRefresh} title="Refresh">
              <RefreshIcon />
            </button>
          </div>
        </div>

        {run.error_message && <div className="cln-summary-error">{run.error_message}</div>}

        <div className="cln-summary-grid">
          <SummaryStat label="Panels" value={run.panels_total} />
          <SummaryStat label="Polled" value={run.panels_done} variant="sage" />
          <SummaryStat
            label="Errored"
            value={run.panels_errored}
            variant={run.panels_errored > 0 ? 'rose' : 'muted'}
          />
          <SummaryStat label="Domains" value={run.domains_total} variant="ink" hint="across all panels" />
        </div>
      </section>

      <section className="cln-card">
        <div className="cln-card-header">
          <h2 className="cln-card-title">Panel results ({items.length})</h2>
        </div>

        {items.length === 0 && <div className="cln-empty"><span>No panels in this run.</span></div>}

        {items.length > 0 && (
          <table className="cln-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>Status</th>
                <th className="cln-col-num">Domains</th>
                <th>Primary</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((it) => {
                const isBad = it.status === 'error';
                return (
                  <tr key={it.inventory_panel_id} className={isBad ? 'cln-row--bad' : undefined}>
                    <td className="cln-cell-mono">{it.panel_reseller_id ?? '—'}</td>
                    <td className="cln-cell-mono">{it.panel_email}</td>
                    <td>
                      <span className={`cln-badge cln-badge--${it.status}`}>{it.status}</span>
                    </td>
                    <td className="cln-col-num">{it.domains_found ?? '—'}</td>
                    <td className="cln-cell-mono cln-cell-faint">{it.primary_domain ?? '—'}</td>
                    <td className="cln-cell-faint">
                      {it.error_code && <span className="cln-error-tag">{it.error_code}</span>}
                      {it.error_message && <span className="cln-error-msg"> {it.error_message}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

function SummaryStat({
  label,
  value,
  variant = 'default',
  hint,
}: {
  label: string;
  value: number;
  variant?: 'default' | 'sage' | 'rose' | 'honey' | 'muted' | 'ink';
  hint?: string;
}): JSX.Element {
  return (
    <div className="cln-summary-stat">
      <span className="cln-summary-stat-label">{label}</span>
      <span className={`cln-summary-stat-value cln-summary-stat-value--${variant}`}>{value}</span>
      {hint && <span className="cln-summary-stat-hint">{hint}</span>}
    </div>
  );
}
